import { PropsWithChildren, useEffect, useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import { useLocation, useNavigate } from 'react-router-dom';

import { is403Error } from '@cast/api-client';
import {
  determineCurrentOrganization,
  useOrganizations,
} from '@cast/shared-organization-context-data-access';
import {
  useRbacEnterpriseRoleBindings,
  useRbacRoleBindings,
  useRbacRolesQuery,
} from '@cast/shared-rbac-data-access';
import { Organization } from '@cast/types';
import {
  RbacServiceAPIListRolesTypeEnum,
  RoleBinding,
} from '@cast/types-organization-management';

import { AccessControlContext } from './AccessControlContext';
import {
  getEnterpriseInviteMessage,
  NO_ORGANIZATION_ACCESS_MESSAGE,
} from './forbiddenMessages';
import { processRbacData } from './processRbacData';
import { ControlDataBySubjectId } from './types';

type Props = {
  useCurrentUser?: boolean;
  user?: { guid: string } | null;
};

export const AccessControlProvider = ({
  useCurrentUser,
  user,
  children,
}: PropsWithChildren<Props>) => {
  const { currentOrganization, organizations, setCurrentOrganization } =
    useOrganizations();

  const {
    isLoadingRoleBindings: isLoadingOrgRoleBindings,
    roleBindings: orgRoleBindings,
    isRoleBindingsError: isOrgRoleBindingsError,
    roleBindingsError: orgRoleBindingsError,
    refetchRoleBindings: refetchOrgRoleBindings,
  } = useRbacRoleBindings({
    enabled: !!user,
    organizationId: currentOrganization?.id,
    subjectId: useCurrentUser && user?.guid ? [user.guid] : undefined,
    shouldTriggerRetry: useCurrentUser,
  });

  const orgRoleBindingsLoaded =
    !isLoadingOrgRoleBindings && !isOrgRoleBindingsError;

  const {
    isLoadingRoleBindings: isLoadingEnterpriseRoleBindings,
    roleBindings: enterpriseRoleBindings,
    isRoleBindingsError: isEnterpriseRoleBindingsError,
    roleBindingsError: enterpriseRoleBindingsError,
    refetchRoleBindings: refetchEnterpriseRoleBindings,
  } = useRbacEnterpriseRoleBindings({
    // Waits for the org call: once it passes, the user's permissions are in place
    enabled:
      !!user &&
      !!currentOrganization?.id &&
      !!currentOrganization?.parentId &&
      orgRoleBindingsLoaded,
    organizationId: useCurrentUser
      ? ([currentOrganization?.parentId, currentOrganization?.id].filter(
          Boolean
        ) as string[])
      : undefined,
    subjectId: useCurrentUser && user?.guid ? [user.guid] : undefined,
    shouldTriggerRetry: useCurrentUser && isEmpty(orgRoleBindings),
  });

  const {
    isLoading: isLoadingRoles,
    data: roles,
    isError: rolesError,
    refetch: refetchRoles,
    isSuccess: isSuccessRoles,
  } = useRbacRolesQuery({
    type: RbacServiceAPIListRolesTypeEnum.ALL,
  });

  const rolesById = keyBy(roles, 'id');

  const accessControlData: ControlDataBySubjectId = useMemo(() => {
    const allRoleBindings = [
      ...(orgRoleBindings || []),
      ...(enterpriseRoleBindings || []),
    ].filter((binding): binding is RoleBinding => !!binding);

    const currentUserId = useCurrentUser ? user?.guid : undefined;
    return processRbacData({
      roleBindings: allRoleBindings,
      rolesById,
      organizations: organizations ?? [],
      currentUserId,
    });
  }, [
    orgRoleBindings,
    enterpriseRoleBindings,
    rolesById,
    organizations,
    useCurrentUser,
    user?.guid,
  ]);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for 403 errors on organization role bindings (e.g., IP-based access restrictions)
    const hasOrg403 = is403Error(orgRoleBindingsError);

    // If organization role bindings query returns 403, redirect to forbidden
    if (hasOrg403 && location.pathname !== '/forbidden') {
      navigate('/forbidden', {
        state: {
          message: NO_ORGANIZATION_ACCESS_MESSAGE,
        },
      });
      return; // Early return to prevent further checks
    }

    const enterpriseRoleBindingsLoaded =
      !isLoadingEnterpriseRoleBindings &&
      (!isEnterpriseRoleBindingsError ||
        is403Error(enterpriseRoleBindingsError)); // 403 means the user has no enterprise-level bindings

    if (
      isEmpty(accessControlData) &&
      orgRoleBindingsLoaded &&
      (enterpriseRoleBindingsLoaded || !currentOrganization?.parentId) &&
      isSuccessRoles &&
      organizations?.length &&
      currentOrganization?.id &&
      location.pathname !== '/forbidden'
    ) {
      const orgToSet = determineCurrentOrganization(
        organizations,
        currentOrganization?.id,
        currentOrganization?.parentId ?? undefined
      );
      if (orgToSet) {
        // Skipping redirect as it would redirect to /dashboard page
        setCurrentOrganization(orgToSet.id, { skipRedirect: true });
      }
      if (
        orgToSet?.type === 'ORGANIZATION_TYPE_CHILD' &&
        organizations.find((o: Organization) => o.id === orgToSet.parentId)
      ) {
        const parentOrgName = organizations.find(
          (o: Organization) => o.id === orgToSet.parentId
        )?.name;
        navigate('/forbidden', {
          state: {
            message: getEnterpriseInviteMessage(parentOrgName!),
          },
        });
      } else {
        navigate('/forbidden', {
          state: {
            message: NO_ORGANIZATION_ACCESS_MESSAGE,
          },
        });
      }
    }
  }, [
    accessControlData,
    orgRoleBindingsLoaded,
    orgRoleBindingsError,
    isLoadingEnterpriseRoleBindings,
    isEnterpriseRoleBindingsError,
    enterpriseRoleBindingsError,
    isSuccessRoles,
    navigate,
    location,
    organizations,
    currentOrganization?.id,
    setCurrentOrganization,
    currentOrganization,
  ]);

  const isLoading =
    isLoadingOrgRoleBindings ||
    isLoadingEnterpriseRoleBindings ||
    isLoadingRoles;
  const error =
    isOrgRoleBindingsError || isEnterpriseRoleBindingsError || rolesError;
  const refetch = async () => {
    await Promise.all([
      refetchOrgRoleBindings(),
      refetchEnterpriseRoleBindings(),
      refetchRoles(),
    ]);
  };

  return (
    <AccessControlContext.Provider
      value={{
        isLoading,
        accessControlData,
        error,
        refetch,
      }}
    >
      {children}
    </AccessControlContext.Provider>
  );
};
